import React from "react";
import {
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { useSystemsContext } from "../../../context/SystemsContext"; // 1. Import the context hook

// The timezones array is a constant and can live outside the component
const timezones = [
  { label: "Eastern", value: "America/Indiana/Indianapolis" },
  { label: "Central", value: "America/Chicago" },
  { label: "Mountain", value: "America/Denver" },
  { label: "Arizona", value: "America/Phoenix" },
  { label: "Pacific", value: "America/Los_Angeles" },
];

const systemCategories = [
  "Car Dealers",
  "Alliance Auto Autions",
  "America's Auto Actions",
  "Dealers Auto Auctions",
  "Manheim Auto Auctions",
  "Metro Auto Auctions",
  "Boats",
  "RV",
  "Internal",
  "Other"
];

// 2. Remove all props, the component is now self-sufficient
const SystemSettingsForm: React.FC = () => {
  // 3. Get all the data and functions needed directly from the context
  const { formData, handleChange } = useSystemsContext();

  return (
    <Paper sx={{ p: 3 }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        System Settings
      </Typography>

      <Grid container spacing={2}>
        {/* System Code */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="System Code"
            inputProps={{ maxLength: 15 }}
            value={formData.systemCode || ""}
            onChange={(e) => handleChange("systemCode", e.target.value)}
          />
        </Grid>

        {/* Common Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            label="Common Name"
            value={formData.commonName || ""}
            onChange={(e) => handleChange("commonName", e.target.value)}
          />
        </Grid>
        
        {/* System Category Dropdown (New Field) */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            required
            label="System Category"
            // Default to "Other" if the category is not set on the system record
            value={formData.system_category || "Other"}
            onChange={(e) => handleChange("system_category", e.target.value)}
          >
            {systemCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Asset URL */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Asset URL"
            value={formData.cloudFrontBaseUrl || "assets.bwiportal.com"}
            onChange={(e) => handleChange("cloudFrontBaseUrl", e.target.value)}
          />
        </Grid>

        {/* Zoho Deal Identifier */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            required
            type="text"
            label="Zoho Deal Identifier"
            inputProps={{ maxLength: 19, inputMode: "numeric", pattern: "[0-9]*" }}
            value={formData.customerDealIdentifier || ""}
            onChange={(e) => handleChange("customerDealIdentifier", e.target.value)}
          />
        </Grid>

        {/* Daily Report Email */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Daily Report Email"
            placeholder="Optional"
            value={formData.dailyAssetReportEmailList || ""}
            onChange={(e) =>
              handleChange("dailyAssetReportEmailList", e.target.value)
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="iFrame Location ID"
            placeholder="Optional"
            value={formData.iframeLocationID || ""}
            onChange={(e) => handleChange("iframeLocationID", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            disabled // This makes the field read-only and visually distinct
            label="UMS Environment"
            value={formData.ums_env || ""}
            // Add a helper text to explain why it's disabled
            helperText="This value is set automatically by the system."
          />
        </Grid>

        {/* Timezone Dropdown */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            required
            label="System Timezone"
            value={formData.IANA_timezone || ""}
            onChange={(e) => handleChange("IANA_timezone", e.target.value)}
          >
            {timezones.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SystemSettingsForm;